/**
 * WHAT:
 *   Custom hook for automatically loading securities data when ISIN is available.
 *
 * WHY:
 *   Encapsulates the logic for enriching parsed PDF data with securities information.
 *
 * DOMAIN: Investments → Parqet → Test Tab → Hooks
 *
 * DEPENDENCIES:
 *   - Tauri core API (invoke)
 *   - Supabase securities API (getSecuritiesFromSupabase)
 *   - React useEffect hook
 *
 * RISKS:
 *   - WARN: Supabase API calls may fail silently
 *   - NOTE: Multiple API calls may be triggered if ISIN changes rapidly
 *   - NOTE: Loading state may not properly reset on errors
 *   - WARN: OpenFIGI API calls may hit rate limits
 */

import type { TestTabState, SecuritiesDataEntry } from "../types";

import { invoke } from "@tauri-apps/api/core";
import { useEffect } from "react";

import { getSecuritiesFromSupabase } from "@/domains/investments/parqet/securities/services/api";
import { debugLog } from "@/domains/logging/utils/debugLog";

/**
 * WHAT:
 *   Custom hook that automatically loads securities data when ISIN is detected.
 *
 * WHY:
 *   Provides automatic enrichment workflow matching the import logic.
 *
 * INPUTS:
 *   - state: TestTabState — State object containing all state values and setters
 *
 * BEHAVIOR:
 *   - Monitors current PDF and rawData for ISIN
 *   - Automatically loads securities data via OpenFIGI if enrichment enabled
 *   - Falls back to Supabase if enrichment is skipped
 *   - Updates securitiesDataMap with loaded data
 *
 * RISKS:
 *   - NOTE: May trigger multiple API calls if ISIN changes rapidly
 *   - WARN: OpenFIGI API may have rate limits
 *
 * @param state
 *
 */
export const useSecuritiesEnrichment = (state: TestTabState): void => {
  const {
    browseFailedOnly,
    currentPdfIndex,
    failedPdfs,
    parsedPdfs,
    rawData,
    securitiesDataMap,
    setIsLoadingSecurities,
    setSecuritiesDataMap,
    skipEnrichment,
  } = state;

  useEffect(() => {
    // Prüfe sowohl currentPdf als auch rawData
    const currentList = browseFailedOnly ? failedPdfs : parsedPdfs;
    const currentPdf = currentList[currentPdfIndex];
    const displayData = currentPdf ? (currentPdf.data ?? currentPdf) : null;
    const rawDataIsin = rawData?.isin;
    const currentPdfIsin = displayData?.isin;

    // Verwende ISIN aus rawData (wenn vorhanden) oder aus currentPdf
    const isinRaw = rawDataIsin ?? currentPdfIsin;
    const isin = typeof isinRaw === "string" ? isinRaw : null;

    // Debug: Logge Bedingung
    const hasIsin = isin !== null && isin !== "";
    const hasSecuritiesData = isin !== null && isin in securitiesDataMap;
    const willCallEnrich = hasIsin && !hasSecuritiesData && !skipEnrichment;

    debugLog("debug", "investments", "[ENRICHMENT] useEffect check", {
      currentPdfIsin,
      hasIsin,
      hasSecuritiesData,
      isin,
      module: "parqet",
      rawDataIsin,
      skipEnrichment,
      willCallEnrich,
    });

    if (isin !== null && !securitiesDataMap[isin] && !skipEnrichment) {
      // Lade Securities-Daten automatisch - vollständiger Enrichment-Workflow wie beim Import
      debugLog("debug", "investments", "Loading securities for ISIN", {
        file: "useSecuritiesEnrichment.ts",
        function: "useSecuritiesEnrichment",
        isin,
        module: "parqet",
        skipEnrichment,
      });
      setIsLoadingSecurities(true);

      interface EnrichmentResult {
        results?: null | {
          securities?: Record<string, SecuritiesDataEntry>;
        };
      }

      // Verwende enrich_securities_openfigi mit den gleichen Parametern wie beim Import
      debugLog("debug", "investments", "Calling enrich_securities_openfigi for ISIN", {
        file: "useSecuritiesEnrichment.ts",
        function: "useSecuritiesEnrichment",
        isin,
        module: "parqet",
      });
      void invoke<EnrichmentResult>("enrich_securities_openfigi", {
        apiKey: null,
        dividendScheduleLimit: 10, // Letzte 10 Dividenden
        includeDividendSchedule: true, // Für vollständige Test-Ansicht aktiviert
        includeMetrics: true, // Wie beim Import
        includePriceCache: true, // Für vollständige Test-Ansicht aktiviert
        includeReferenceData: true,
        isins: [isin],
        priceCacheDays: 30, // 30 Tage Preis-Historie
      })
        .then((result: EnrichmentResult) => {
          // Reason: Type guard for result structure
          const hasResults = result.results !== null && result.results !== undefined;
          const securitiesMap = result.results?.securities;
          const securitiesEntry = isin ? securitiesMap?.[isin] : undefined;
          const hasSecurities = hasResults && securitiesMap !== undefined;
          const isinInResults = hasSecurities && isin && isin in securitiesMap;

          debugLog("debug", "investments", "enrich_securities_openfigi result", {
            file: "useSecuritiesEnrichment.ts",
            function: "useSecuritiesEnrichment",
            hasResults,
            hasSecurities,
            isinInResults,
            module: "parqet",
          });

          if (securitiesEntry && typeof securitiesEntry === "object" && isin) {
            const entryTyped = securitiesEntry;
            debugLog("debug", "investments", "Found securities data for ISIN", {
              file: "useSecuritiesEnrichment.ts",
              function: "useSecuritiesEnrichment",
              hasDividends: entryTyped.dividend_schedule !== undefined,
              hasMetrics: entryTyped.securities_metrics !== undefined,
              hasPriceCache: entryTyped.price_cache !== undefined,
              hasSecurities: entryTyped.securities !== undefined,
              isin,
              module: "parqet",
            });
            setSecuritiesDataMap((previous) => ({
              ...previous,
              [isin]: entryTyped,
            }));
          } else {
            debugLog("debug", "investments", "No securities data found for ISIN", {
              file: "useSecuritiesEnrichment.ts",
              function: "useSecuritiesEnrichment",
              isin,
              module: "parqet",
            });
          }
        })
        .catch((error: unknown) => {
          const errorMessage = error instanceof Error ? error.message : String(error);
          debugLog("error", "investments", "Failed to load securities data", {
            error: errorMessage,
            file: "useSecuritiesEnrichment.ts",
            function: "useSecuritiesEnrichment",
            module: "parqet",
          });
        })
        .finally(() => {
          setIsLoadingSecurities(false);
        });
    } else if (isin !== null && !securitiesDataMap[isin] && skipEnrichment) {
      // Wenn Enrichment deaktiviert ist, versuche trotzdem aus Supabase zu laden
      debugLog("debug", "investments", "Loading securities from Supabase (enrichment skipped)", {
        file: "useSecuritiesEnrichment.ts",
        function: "useSecuritiesEnrichment",
        isin,
        module: "parqet",
      });
      if (!isin) {
        return;
      }
      void getSecuritiesFromSupabase([isin])
        .then((result) => {
          const securitiesMap = result.results.securities;

          debugLog("debug", "investments", "getSecuritiesFromSupabase result", {
            file: "useSecuritiesEnrichment.ts",
            function: "useSecuritiesEnrichment",
            hasResults: true,
            hasSecurities: true,
            isinInResults: isin in securitiesMap,
            module: "parqet",
          });

          if (isin in securitiesMap) {
            const securitiesEntry = securitiesMap[isin] as SecuritiesDataEntry | undefined;
            if (securitiesEntry) {
              setSecuritiesDataMap((previous) => ({
                ...previous,
                [isin]: securitiesEntry,
              }));
            }
          } else {
            debugLog("debug", "investments", "No securities data found for ISIN", {
              file: "useSecuritiesEnrichment.ts",
              function: "useSecuritiesEnrichment",
              isin,
              module: "parqet",
            });
          }
        })
        .catch((error: unknown) => {
          const errorMessage = error instanceof Error ? error.message : String(error);
          debugLog("error", "investments", "Failed to load securities data", {
            error: errorMessage,
            file: "useSecuritiesEnrichment.ts",
            function: "useSecuritiesEnrichment",
            module: "parqet",
          });
        });
    }
  }, [
    currentPdfIndex,
    parsedPdfs,
    browseFailedOnly,
    rawData,
    skipEnrichment,
    securitiesDataMap,
    failedPdfs,
  ]);
};
